import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import posthog from '@/lib/posthog';
import { HelmetProvider } from 'react-helmet-async';
import React, { Suspense, lazy } from 'react';

// Lazily load page components
const NotFound = lazy(() => import("@/pages/not-found"));
const HomePage = lazy(() => import("@/pages"));
const ChatEarlyAccessPage = lazy(() => import("@/pages/ChatEarlyAccessPage"));
const ProductPage = lazy(() => import("@/pages/product/ProductPage"));
const RobotCompanionPage = lazy(() => import("@/pages/product/RobotCompanionPage"));
const ResourcesPage = lazy(() => import("@/pages/ResourcesPage"));
const BlogFeedPage = lazy(() => import("@/pages/resources/blogs/BlogFeedPage"));
const BlogPostPage = lazy(() => import("@/pages/resources/blogs/BlogPostPage"));
const CaseStudiesFeedPage = lazy(() => import("@/pages/CaseStudiesFeedPage"));
const CaseStudyPage = lazy(() => import("@/pages/CaseStudyPage"));
const ToolsFeedPage = lazy(() => import("@/pages/ToolsFeedPage"));
const ToolPage = lazy(() => import("@/pages/ToolPage"));
const WaitlistPage = lazy(() => import("@/pages/WaitlistPage"));
const DeliveryTermsPage = lazy(() => import("@/pages/legal/DeliveryTermsPage"));
const ReturnPolicyPage = lazy(() => import("@/pages/legal/ReturnPolicyPage"));
const AccessibilityPage = lazy(() => import("@/pages/AccessibilityPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/legal/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/legal/TermsOfServicePage"));
const ProfessionalFactSheetPage = lazy(() => import("@/pages/legal/ProfessionalFactSheetPage"));
const MeditationsPage = lazy(() => import("@/pages/MeditationsPage"));
const TestPage = lazy(() => import('./pages/test'));
const FamilyStoriesPage = lazy(() => import('@/pages/family-stories'));
const PhoneBackgroundsPage = lazy(() => import('@/pages/PhoneBackgroundsPage'));

/**
 * AppRoutes - Component for application routing
 * 
 * @component
 */
function AppRoutes() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Switch>
        <Route path="/" component={HomePage} />
        <Route path="/chat" component={ChatEarlyAccessPage} />
        <Route path="/product" component={ProductPage} />
        <Route path="/product/robot-companion" component={RobotCompanionPage} />
        <Route path="/resources" component={ResourcesPage} />
        <Route path="/blog" component={BlogFeedPage} />
        <Route path="/blog/:id" component={BlogPostPage} />
        <Route path="/resources/case-studies" component={CaseStudiesFeedPage} />
        <Route path="/resources/case-studies/:id" component={CaseStudyPage} />
        <Route path="/resources/tools" component={ToolsFeedPage} />
        <Route path="/resources/tools/:id" component={ToolPage} />
        <Route path="/waitlist" component={WaitlistPage} />
        <Route path="/delivery-terms" component={DeliveryTermsPage} />
        <Route path="/return-policy" component={ReturnPolicyPage} />
        <Route path="/accessibility" component={AccessibilityPage} />
        <Route path="/privacy-policy" component={PrivacyPolicyPage} />
        <Route path="/terms-of-service" component={TermsOfServicePage} />
        <Route path="/professional-fact-sheet" component={ProfessionalFactSheetPage} />
        <Route path="/meditations" component={MeditationsPage} />
        <Route path="/test" component={TestPage} />
        <Route path="/family-stories" component={FamilyStoriesPage} />
        <Route path="/phone-backgrounds" component={PhoneBackgroundsPage} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

/**
 * App - Main application component
 * 
 * @component
 */
function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AppRoutes />
        <Toaster />
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
